// Informes rápidos: todas las vistas salen de los mismos resúmenes diarios por
// unidad (DailyUnitSummary). Lo que decide qué se ve vive acá, puro y con tests
// (quickReports.compute.test.ts): la UI sólo memoiza y pinta.
import type { DailyUnitSummary, Place, QuickPeriod } from "./types";

export const DEFAULT_TZ = "America/Mexico_City";

// Zona del navegador: "Hoy" y las horas se leen en la hora local de quien mira
// (decisión de Daniel, 17-oct-2026). Se manda como tz en cada petición.
export const userTz = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TZ;
  } catch {
    return DEFAULT_TZ;
  }
};
// El backend precalcula como máximo 15 días: un periodo mayor ya no es "rápido".
export const MAX_DAYS = 15;

// ---------- Periodo ----------

// YYYY-MM-DD del instante `now` en la zona `tz` (en-CA ya da ese formato).
export const dateInTz = (now: number, tz: string): string =>
  new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);

// Aritmética de fechas de calendario en UTC: no depende de la zona del navegador.
export const addDays = (date: string, n: number): string => {
  const [y, m, d] = date.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};

export const daysBetween = (from: string, to: string): string[] => {
  const out: string[] = [];
  for (let d = from; d <= to && out.length < MAX_DAYS; d = addDays(d, 1)) out.push(d);
  return out;
};

const PERIOD_SPAN: Record<QuickPeriod, { back: number; len: number }> = {
  today: { back: 0, len: 1 },
  yesterday: { back: 1, len: 1 },
  last3: { back: 0, len: 3 },
  last15: { back: 0, len: MAX_DAYS },
};

export const periodRange = (period: QuickPeriod, tz: string, now = Date.now()) => {
  const to = addDays(dateInTz(now, tz), -PERIOD_SPAN[period].back);
  const from = addDays(to, -(PERIOD_SPAN[period].len - 1));
  return { from, to, days: daysBetween(from, to) };
};

// Un periodo de varios días que termina hoy se pide en dos partes: los días
// cerrados (precalculados, ~1 s) y «Hoy» (se calcula al vuelo, ~3 s). Así los
// cerrados se muestran sin esperar a hoy. null = una sola petición.
export const splitToday = (range: { from: string; to: string; days: string[] }, today: string) =>
  range.days.length > 1 && range.to === today
    ? { closed: { from: range.from, to: addDays(today, -1) }, today: { from: today, to: today } }
    : null;

// ---------- Formato ----------

export const fmtKm = (km: number) =>
  `${(km || 0).toLocaleString("es-MX", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`;

export const fmtSpeed = (kmh: number) => `${Math.round(kmh || 0)} km/h`;

export const fmtDuration = (s: number): string => {
  const sec = Math.max(0, Math.round(s || 0));
  if (sec === 0) return "0 min";
  if (sec < 60) return "< 1 min";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
};

export const fmtTime = (ts: number | null | undefined, tz: string) =>
  ts == null ? "—" : new Intl.DateTimeFormat("es-MX", { timeZone: tz, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(ts);

export const fmtDateTime = (ts: number | null | undefined, tz: string) =>
  ts == null
    ? "—"
    : new Intl.DateTimeFormat("es-MX", {
        timeZone: tz,
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      }).format(ts);

// "lun 13 oct" a partir de YYYY-MM-DD, sin pasar por la zona del navegador.
export const fmtDay = (date: string) => {
  const [y, m, d] = date.split("-").map(Number);
  return new Intl.DateTimeFormat("es-MX", { timeZone: "UTC", weekday: "short", day: "numeric", month: "short" }).format(
    Date.UTC(y, m - 1, d),
  );
};

export const placeLabel = (p: Place | null | undefined) =>
  p?.geofence?.name || p?.address || (p ? `${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}` : "—");

// ---------- Unidades ----------

export type UnitRef = { id: string; name: string; plate: string | null };

export const unitsOf = (items: DailyUnitSummary[]): UnitRef[] => {
  const map = new Map<string, UnitRef>();
  for (const it of items)
    if (!map.has(it.device_id)) map.set(it.device_id, { id: it.device_id, name: it.unit_name, plate: it.plate ?? null });
  return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, "es", { numeric: true }));
};

// Unidades desde /deviceList, con la misma regla de nombre que el backend usa
// para unit_name (quickReports/dailyUnitSummaryService.js): la bitácora ya no
// espera el resumen de TODA la flota del periodo sólo para armar su selector.
export type DeviceLite = { id: string; name?: string | null; vehicle?: { name?: string | null; plate?: string | null } | null };
export const unitsFromDevices = (devices: DeviceLite[]): UnitRef[] =>
  devices
    .map((d) => ({ id: d.id, name: d.vehicle?.name || d.name || d.vehicle?.plate || d.id, plate: d.vehicle?.plate || null }))
    .sort((a, b) => a.name.localeCompare(b.name, "es", { numeric: true }));

// Lista vacía = todas las unidades.
export const filterByUnits = (items: DailyUnitSummary[], deviceIds: string[]) => {
  if (!deviceIds.length) return items;
  const set = new Set(deviceIds);
  return items.filter((it) => set.has(it.device_id));
};

const byUnit = (items: DailyUnitSummary[]) => {
  const map = new Map<string, DailyUnitSummary[]>();
  for (const it of items) {
    const list = map.get(it.device_id);
    if (list) list.push(it);
    else map.set(it.device_id, [it]);
  }
  return [...map.values()].sort((a, b) => a[0].unit_name.localeCompare(b[0].unit_name, "es", { numeric: true }));
};

const sum = <T>(list: T[], f: (x: T) => number) => list.reduce((acc, x) => acc + (f(x) || 0), 0);

// ---------- Resumen de flota ----------

export type FleetRow = {
  id: string;
  unit: string;
  plate: string | null;
  km: number;
  driving_s: number;
  maneuver_s: number;
  idle_s: number;
  stopped_s: number;
  rest_s: number;
  no_data_s: number;
  stops: number;
  max_kmh: number;
  overspeed: number;
};

export const fleetSummary = (items: DailyUnitSummary[]) => {
  const rows: FleetRow[] = byUnit(items).map((days) => ({
    id: days[0].device_id,
    unit: days[0].unit_name,
    plate: days[0].plate ?? null,
    km: sum(days, (d) => d.distance_km),
    driving_s: sum(days, (d) => d.driving_s),
    maneuver_s: sum(days, (d) => d.maneuver_s),
    idle_s: sum(days, (d) => d.idle_s),
    stopped_s: sum(days, (d) => d.stopped_s),
    rest_s: sum(days, (d) => d.rest_s),
    no_data_s: sum(days, (d) => d.no_data_s),
    stops: sum(days, (d) => d.stops_count),
    max_kmh: Math.max(0, ...days.map((d) => d.max_speed_kmh || 0)),
    overspeed: sum(days, (d) => d.overspeed?.count),
  }));
  const totals = {
    units: rows.length,
    active: rows.filter((r) => r.km > 0).length,
    km: sum(rows, (r) => r.km),
    driving_s: sum(rows, (r) => r.driving_s),
    maneuver_s: sum(rows, (r) => r.maneuver_s),
    idle_s: sum(rows, (r) => r.idle_s),
    rest_s: sum(rows, (r) => r.rest_s),
    no_data_s: sum(rows, (r) => r.no_data_s),
    stops: sum(rows, (r) => r.stops),
    max_kmh: Math.max(0, ...rows.map((r) => r.max_kmh)),
    overspeed: sum(rows, (r) => r.overspeed),
  };
  return { rows, totals };
};

// ---------- Kilometraje ----------

// Un solo día (Hoy/Ayer): km por hora; si no, unidades × días.
export const mileageMatrix = (items: DailyUnitSummary[], days: string[]) => {
  const hourly = days.length === 1;
  const columns = hourly ? Array.from({ length: 24 }, (_, h) => String(h).padStart(2, "0")) : days;
  const rows = byUnit(items).map((list) => {
    const values = hourly
      ? Array.from({ length: 24 }, (_, h) => sum(list, (d) => d.hourly_km?.[h] ?? 0))
      : days.map((day) => sum(list.filter((d) => d.date === day), (d) => d.distance_km));
    return { id: list[0].device_id, unit: list[0].unit_name, values, total: sum(values, (v) => v) };
  });
  const colTotals = columns.map((_, i) => sum(rows, (r) => r.values[i]));
  const max = Math.max(0, ...rows.flatMap((r) => r.values));
  return { hourly, columns, rows, colTotals, total: sum(rows, (r) => r.total), max };
};

// Km de una celda (una hora o un día de una unidad). Con el redondeo a entero,
// 0.4 km de maniobra se leía "0" en una celda con movimiento: un decimal y,
// debajo de 0.1, "<0.1".
export const fmtKmShort = (km: number) =>
  !(km > 0) ? "0" : km < 0.1 ? "<0.1" : km.toLocaleString("es-MX", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

// Kilometraje en gráficas comunes: unidades de mayor a menor y, por hora o por
// día, las `top` unidades con más km apiladas más "Otras N unidades". Con 18
// unidades, 18 colores apilados no se leen; si sólo sobraría una, va con nombre.
export const mileageCharts = (m: ReturnType<typeof mileageMatrix>, top = 3) => {
  const ranked = [...m.rows].sort((a, b) => b.total - a.total || a.unit.localeCompare(b.unit, "es", { numeric: true }));
  const lead = ranked.slice(0, ranked.length > top + 1 ? top : ranked.length);
  const rest = ranked.slice(lead.length);
  const series = [
    ...lead.map((r) => ({ id: r.id, name: r.unit, values: r.values })),
    ...(rest.length ? [{ id: "others", name: `Otras ${rest.length} unidades`, values: m.columns.map((_, i) => sum(rest, (r) => r.values[i])) }] : []),
  ];
  const columnMax = Math.max(0, ...m.colTotals);
  return {
    ranked,
    series,
    columnMax,
    active: ranked.filter((r) => r.total > 0).length,
    // Hora o día con más km de toda la flota; -1 si nadie se movió.
    peak: columnMax > 0 ? m.colTotals.indexOf(columnMax) : -1,
  };
};

// ---------- Paradas ----------

export type StopRow = { id: string; unit: string; start_ts: number; end_ts: number; duration_s: number; place: Place };

export const stopsList = (items: DailyUnitSummary[], minDurationS = 0): StopRow[] =>
  items
    .flatMap((it) =>
      it.stops
        .filter((s) => s.duration_s >= minDurationS)
        .map((s, i) => ({ id: `${it.device_id}-${it.date}-${i}`, unit: it.unit_name, ...s })),
    )
    .sort((a, b) => b.start_ts - a.start_ts);

// En periodos de varios días el backend no manda tramos, viajes ni paradas
// (segments_omitted): el detalle de cada parada sólo existe por día.
export const detailOmitted = (items: DailyUnitSummary[]) => items.some((it) => it.segments_omitted);

// Paradas por unidad con los totales del día, que sí vienen en cualquier periodo.
export const stopsByUnit = (items: DailyUnitSummary[]) =>
  byUnit(items)
    .map((days) => ({
      id: days[0].device_id,
      unit: days[0].unit_name,
      stops: sum(days, (d) => d.stops_count),
      stopped_s: sum(days, (d) => d.stopped_s),
      rest_s: sum(days, (d) => d.rest_s),
    }))
    .sort((a, b) => b.stops - a.stops);

// ---------- Geocercas ----------

export type GeofenceVisitRow = {
  id: string;
  unit: string;
  geofence: string;
  enter_ts: number | null;
  exit_ts: number | null;
  duration_s: number;
};

export const geofenceVisits = (items: DailyUnitSummary[]) => {
  const visits: GeofenceVisitRow[] = items
    .flatMap((it) =>
      it.geofences.map((g, i) => ({
        id: `${it.device_id}-${it.date}-${g.id}-${i}`,
        unit: it.unit_name,
        geofence: g.name,
        enter_ts: g.enter_ts,
        exit_ts: g.exit_ts,
        duration_s: g.duration_s,
      })),
    )
    .sort((a, b) => (b.enter_ts ?? b.exit_ts ?? 0) - (a.enter_ts ?? a.exit_ts ?? 0));
  const byFence = new Map<string, { id: string; geofence: string; visits: number; duration_s: number; units: Set<string> }>();
  for (const v of visits) {
    const g = byFence.get(v.geofence) ?? { id: v.geofence, geofence: v.geofence, visits: 0, duration_s: 0, units: new Set<string>() };
    g.visits += 1;
    g.duration_s += v.duration_s;
    g.units.add(v.unit);
    byFence.set(v.geofence, g);
  }
  const summary = [...byFence.values()]
    .map(({ units, ...g }) => ({ ...g, units: units.size }))
    .sort((a, b) => b.duration_s - a.duration_s);
  return { visits, summary };
};

// ---------- Excesos de velocidad ----------

export const overspeedByUnit = (items: DailyUnitSummary[]) =>
  byUnit(items)
    .map((days) => ({
      id: days[0].device_id,
      unit: days[0].unit_name,
      limit_kmh: Math.max(0, ...days.map((d) => d.overspeed?.limit_kmh || 0)),
      count: sum(days, (d) => d.overspeed?.count),
      max_kmh: Math.max(0, ...days.map((d) => d.overspeed?.max_kmh || 0)),
      duration_s: sum(days, (d) => d.overspeed?.duration_s),
    }))
    .sort((a, b) => b.count - a.count || b.max_kmh - a.max_kmh);

// ---------- Alertas ----------

// "gps" es la columna «Pérdida de señal»: en producción el backend nunca emite
// gps_lost; lo que se veía como "sin GPS" era la bandera gsm/umts_jamming, y
// ahora llega contado por episodios (rachas separadas por 15 min o más), no por
// posición. gps_lost se sigue sumando ahí por si algún equipo lo manda.
// power_connection es la vuelta a la normalidad, no una alerta, y queda fuera
// igual que cualquier tipo desconocido.
export type AlertKind = "sos" | "power" | "gps";
export const alertKind = (type: string): AlertKind | null =>
  type === "sos" ? "sos" : type === "power_disconnection" ? "power" : type === "jamming" || type === "gps_lost" ? "gps" : null;

export type AlertRow = { id: string; device: string; unit: string; ts: number; kind: AlertKind; type: string; place: Place | null };

export const alertsReport = (items: DailyUnitSummary[]) => {
  const list: AlertRow[] = items
    .flatMap((it) =>
      it.alerts.flatMap((a, i) => {
        const kind = alertKind(a.type);
        return kind ? [{ id: `${it.device_id}-${a.ts}-${i}`, device: it.device_id, unit: it.unit_name, ts: a.ts, kind, type: a.type, place: a.place ?? null }] : [];
      }),
    )
    .sort((a, b) => b.ts - a.ts);
  const counts = new Map<string, { id: string; unit: string; sos: number; power: number; gps: number; total: number }>();
  for (const a of list) {
    const c = counts.get(a.device) ?? { id: a.device, unit: a.unit, sos: 0, power: 0, gps: 0, total: 0 };
    c[a.kind] += 1;
    c.total += 1;
    counts.set(a.device, c);
  }
  const byUnitRows = [...counts.values()].sort((a, b) => b.sos - a.sos || b.total - a.total);
  return {
    list,
    byUnit: byUnitRows,
    totals: { sos: sum(byUnitRows, (r) => r.sos), power: sum(byUnitRows, (r) => r.power), gps: sum(byUnitRows, (r) => r.gps) },
  };
};

// ---------- Plazo de las peticiones ----------

// Corta una petición a los `ms` aunque algo de en medio no respete el timeout
// de axios (p. ej. un interceptor esperando el refresco del token): aborta y
// rechaza, así la pantalla pasa a "Reintentar" y nunca se queda en el esqueleto.
export const withTimeout = <T>(run: (signal: AbortSignal) => Promise<T>, ms: number, outer?: AbortSignal): Promise<T> => {
  const ctrl = new AbortController();
  const abort = () => ctrl.abort();
  if (outer?.aborted) abort();
  outer?.addEventListener("abort", abort);
  let timer: ReturnType<typeof setTimeout> | undefined;
  const expired = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      ctrl.abort();
      reject(new Error(`Sin respuesta en ${Math.round(ms / 1000)} s`));
    }, ms);
  });
  return Promise.race([run(ctrl.signal), expired]).finally(() => {
    clearTimeout(timer);
    outer?.removeEventListener("abort", abort);
  });
};

// ---------- Favoritos dentro de cada sección ----------

// Los favoritos de una sección van primero, en el orden en que se fijaron; el
// resto conserva el orden del catálogo.
export const favoritesFirst = <T extends { id: string }>(list: T[], favorites: string[]): T[] => {
  const rank = (id: string) => {
    const i = favorites.indexOf(id);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  return list
    .map((item, i) => ({ item, i }))
    .sort((a, b) => rank(a.item.id) - rank(b.item.id) || a.i - b.i)
    .map((x) => x.item);
};
